/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the header component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';


import { makeStyles, Theme } from '@material-ui/core/styles';



/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import * as Dtos from '../../api/Neon.Dtos';






import OpmsField from '../../components/OpmsForm/OpmsField';

import OpmsInput from '../../components/OpmsForm/OpmsInput';



import ToggleButtonGroup from '../../components/OpmsForm/Fields/ToggleButtonGroup';

import Typography from '@material-ui/core/Typography';
import PatientBreadcrumbs from '../../components/Breadcrumbs/PatientBreadcrumbs';
import TrialForm from '../../components/OpmsForm/TrialForm';
import { PatientContext } from '@ngt/online-patient-management-ui';
import OpmsConditional, { IOpmsContionParams } from '../../components/OpmsForm/OpmsConditional';
import OpmsFieldGroup from '../../components/OpmsForm/OpmsFieldGroup';
import PageLayout from 'src/components/Layout/PageLayout';
import { Paper } from '@material-ui/core';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface IEligibilityChecklistProps {
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => ({
    container: {
        padding: theme.spacing(0)
    },
    subHeading: {
        padding: theme.spacing(3)
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */

const isPatientCohortC = ({ formState }: IOpmsContionParams<Dtos.EligibilityChecklist>) => formState?.values?.isPatientCohortC === true;
const isPatientNotCohortC = ({ formState }: IOpmsContionParams<Dtos.EligibilityChecklist>) => formState?.values?.isPatientCohortC !== true;
const hbsagPositive = ({ formState }: IOpmsContionParams<Dtos.EligibilityChecklist>) => formState?.values?.activeHepatitisB === Dtos.LookupYesNoType.Yes;


/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

const EligibilityChecklist: React.FunctionComponent<IEligibilityChecklistProps> = () => {
    const classes = useStyles();

    const [patient] = React.useContext(PatientContext);

    const patientCohortC = (patient as Dtos.Patient)?.treatmentId !== 1 && (patient as Dtos.Patient)?.treatmentId !== 2;


    return (
        <PageLayout
            breadcrumbs={
                <PatientBreadcrumbs />
            }
            heading="Eligibility Checklist"
        >
            <Paper
                className={classes.container}
            >
                <TrialForm
                    formClass={Dtos.EligibilityChecklist}
                >
                    <Typography
                        variant="h2"
                        color="primary"
                        className={classes.subHeading}
                    >
                        Inclusion Criteria
                    </Typography>
                    <OpmsField name="TumourBlockOrRepresentativeAvailableId">
                        <OpmsInput
                            component={ToggleButtonGroup}
                        />
                    </OpmsField>
                    <OpmsField name="PreviouslyUntreatedBreastCancerId">
                        <OpmsInput
                            component={ToggleButtonGroup}
                        />
                    </OpmsField>
                    <OpmsField name="AgreeToCoreNeedleBiopsiesId">
                        <OpmsInput
                            component={ToggleButtonGroup}
                        />
                    </OpmsField>
                    <OpmsField
                        name="CommenceStudyTreatmentOnScheduleId"
                        label={
                            patientCohortC ?
                                <Typography>Is the patient able to commence study treatment within 14 days of registration?</Typography>
                                :
                                undefined
                        }
                    >
                        <OpmsInput
                            component={ToggleButtonGroup}
                        />
                    </OpmsField>
                    <OpmsField name="AwareOfSurgeryScheduleId">
                        <OpmsInput
                            component={ToggleButtonGroup}
                        />
                    </OpmsField>
                    <OpmsField
                        name="InformedAndAgreedToAvoidPregnancyId"
                        label={
                            patientCohortC ? 
                                <Typography>
                                    Has the participant been informed of and has agreed to use method(s) of non-hormonal contraception from the time of enrolment for the duration of treatment and for at least 5 months after completing nivolumab and/or relatlimab treatment?
                                </Typography>
                                :
                                undefined
                        }
                    >
                        <OpmsInput
                            component={ToggleButtonGroup}
                        />
                    </OpmsField>
                    <Typography
                        variant="h2"
                        color="primary"
                        className={classes.subHeading}
                    >
                        Exclusion Criteria
                    </Typography>
                    <OpmsField
                        name="EvidenceOfBreastCancerId"
                        label={
                            patientCohortC ?
                                <Typography>
                                    Does the patient have evidence of:
                                    <ul>
                                        <li>metastatic breast cancer, or</li>
                                        <li>multifocal or bilateral invasive breast cancer, or</li>
                                        <li>stage 3 or 4 disease, or</li>
                                        <li>tumour of any size considered inoperable at presentation</li>
                                    </ul>
                                </Typography>
                                :
                                undefined
                        }
                    >
                        <OpmsInput
                            component={ToggleButtonGroup}
                        />
                    </OpmsField>
                    <OpmsField name="ReceivedPriorTherapyId">
                        <OpmsInput
                            component={ToggleButtonGroup}
                        />
                    </OpmsField>
                    <OpmsField
                        name="UndergoneLymphNodeBiopsyId"
                        label={
                            patientCohortC ?
                                <Typography>
                                    Has the patient undergone or planned a sentinel lymph node biopsy? 
                                </Typography>
                                :
                                undefined
                        }
                    >
                        <OpmsInput
                            component={ToggleButtonGroup}
                        />
                    </OpmsField>
                    <OpmsField
                        name="ParticipatedOrParticipatingInStudyTherapyId"
                        label={
                            patientCohortC ?
                                <Typography>
                                    Is the patient currently participating and receiving study therapy, or has participated in a study of an investigational agent and received study therapy or used an investigational device within 4 weeks of the expected registration date?
                                </Typography>
                                :
                                undefined
                        }
                        instruction={
                            patientCohortC ? <small>
                                <br />
                                <i>Note: participant will also be excluded if they received an investigational therapeutic agent with <strong>anticancer or anti-proliferative intent within the last 12 months.</strong></i>
                            </small>
                            :
                            undefined
                        }
                    >
                        <OpmsInput
                            component={ToggleButtonGroup}
                        />
                    </OpmsField>
                    <OpmsField
                        name="PlannedOrReceivedLiveVaccineId"
                        label={
                            patientCohortC ?
                                <Typography>
                                    Has the patient received a live vaccine within 30 days of the expected registration date, or planned to receive live vaccine during study treatment? 
                                </Typography>
                                :
                                undefined
                        }
                        instruction={
                            patientCohortC ? <small>
                                <br />
                                <i>Note: Seasonal influenza vaccines for injection are generally inactivated flu vaccines and are allowed, however intranasal influenza vaccines (e.g. FluMist&reg;) are live attenuated vaccines and are not allowed.</i>
                            </small>
                                :
                                undefined
                        }
                    >
                        <OpmsInput
                            component={ToggleButtonGroup}
                        />
                    </OpmsField>
                    <OpmsField
                        name="ReceivedAlternativeMedicinesId"
                        label={
                            patientCohortC ?
                                <Typography>
                                    Has the patient received treatment with botanical preparations and traditional Chinese medicines, intended for general health support or breast cancer treatment, within 7 days of the expected registration date?
                                </Typography>
                                :
                                undefined
                        }
                    >
                        <OpmsInput
                            component={ToggleButtonGroup}
                        />
                    </OpmsField>
                    <OpmsField
                        name="DiagnosedWithPreviousMalignanciesId"
                        label={
                            patientCohortC ?
                                <Typography>
                                    Has the patient been diagnosed with previous malignancies within the previous 3 years before registration, except for locally curable cancers that have been apparently cured, such as basal or squamous cell skin cancer, superficial bladder cancer, or carcinoma in situ of the prostate, cervix, or breast?
                                </Typography>
                                :
                                undefined
                        }
                    >
                        <OpmsInput
                            component={ToggleButtonGroup}
                        />
                    </OpmsField>
                    <OpmsField name="CurrentlyOnAnyConcurrentAntiNeoplasticTherapyId">
                        <OpmsInput
                            component={ToggleButtonGroup}
                        />
                    </OpmsField>
                    <OpmsField name="DiagnosedWithOtherActiveMalignanciesId">
                        <OpmsInput
                            component={ToggleButtonGroup}
                        />
                    </OpmsField>
                    <OpmsField name="KnownOrSuspectedAutoImmuneDiseaseId">
                        <OpmsInput
                            component={ToggleButtonGroup}
                        />
                    </OpmsField>
                    <OpmsField
                        name="RequiresSystemicTreatmentId"
                        label={
                            patientCohortC ?
                                <Typography>
                                    Is the patient suffering a condition requiring systemic treatment with either corticosteroids (&gt;10mg prednisone equivalent) or other immunosuppressive medications within 14 days of registration?
                                </Typography>
                                :
                                undefined
                        }
                    >
                        <OpmsInput
                            component={ToggleButtonGroup}
                        />
                    </OpmsField>
                    <OpmsField name="InterstitialLungDiseaseId">
                        <OpmsInput
                            component={ToggleButtonGroup}
                        />
                    </OpmsField>
                    <OpmsField name="HistoryOfNonInfectiousPneumonitisId">
                        <OpmsInput
                            component={ToggleButtonGroup}
                        />
                    </OpmsField>
                    <OpmsField name="KnownSensitivityToStudyMedicationId">
                        <OpmsInput
                            component={ToggleButtonGroup}
                        />
                    </OpmsField>
                    <OpmsField name="SignificantCardiovascularDiseaseId">
                        <OpmsInput
                            component={ToggleButtonGroup}
                        />
                    </OpmsField>
                    <OpmsField name="TestingPositiveToImmunodeficiencyVirusId">
                        <OpmsInput
                            component={ToggleButtonGroup}
                        />
                    </OpmsField>
                    <OpmsField name="MedicalConditionIncreasingRiskOfParticipationId">
                        <OpmsInput
                            component={ToggleButtonGroup}
                        />
                    </OpmsField>
                    <OpmsConditional
                        formClass={Dtos.EligibilityChecklist}
                        mode="Show"
                        condition={isPatientNotCohortC}
                        subscription={{ values: true }}
                    >
                        <OpmsField name="ActiveHepatitisBOrCId">
                            <OpmsInput
                                component={ToggleButtonGroup}
                            />
                        </OpmsField>
                    </OpmsConditional>
                    <OpmsConditional
                        formClass={Dtos.EligibilityChecklist}
                        mode="Show"
                        condition={isPatientCohortC}
                        subscription={{ values: true }}
                    >
                        <OpmsFieldGroup>
                            <OpmsField name="ActiveHepatitisB">
                                <OpmsInput
                                    component={ToggleButtonGroup}
                                />
                            </OpmsField>
                            <OpmsConditional
                                formClass={Dtos.EligibilityChecklist}
                                mode="Show"
                                condition={hbsagPositive}
                                subscription={{ values: true }}
                            >
                                <OpmsField
                                    name="ParticipantOnHbvAntiviral"
                                    label={
                                        <Typography>
                                            Is hepatitis B virus (HBV) deoxyribonucleic acid (DNA) &lt;500 IU/mL and participant is on antiviral therapy?
                                        </Typography>
                                    }
                                >
                                    <OpmsInput
                                        component={ToggleButtonGroup}
                                    />
                                </OpmsField>
                            </OpmsConditional>    
                        </OpmsFieldGroup>           
                        <OpmsField
                            name="PositiveHepatitisC"
                            instruction={
                                <small>
                                    <br />
                                    <i>Note: If HCV-ribonucleic acid (RNA) is undetectable, select NO.</i>
                                </small>
                            }
                        >
                            <OpmsInput
                                component={ToggleButtonGroup}
                            />
                        </OpmsField>
                        <OpmsField
                            name="ParticipantOnHcvAntiviral"
                            instruction={
                                <small>
                                    <br />
                                    <i>Note: Participants who have completed antiviral therapy for HCV and who have undetectable levels of HCV-RNA are allowed.</i>
                                </small>
                            }
                        >
                            <OpmsInput
                                component={ToggleButtonGroup}
                            />
                        </OpmsField>
                    </OpmsConditional>
                    
                    <OpmsField name="ActiveTuberculosisId">
                        <OpmsInput
                            component={ToggleButtonGroup}
                        />
                    </OpmsField>
                    <OpmsField
                        name="CurrentlyBreastfeedingId"
                        label={
                            patientCohortC ?
                                <Typography>
                                    Is the patient currently pregnant, breastfeeding, or currently using oral, injectable or implant hormonal contraceptives or medicated IUD?
                                </Typography>
                                :
                                undefined
                        }
                    >
                        <OpmsInput
                            component={ToggleButtonGroup}
                        />
                    </OpmsField>
                    <OpmsField name="ConsideredAPoorMedicalRiskId">
                        <OpmsInput
                            component={ToggleButtonGroup}
                        />
                    </OpmsField>
                </TrialForm>
            </Paper>
        </PageLayout>
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */
export default EligibilityChecklist;